import { useState } from 'react'
import { useStore } from '../lib/store'
import { planById } from '../lib/constants'
import { companyFromDomain } from '../lib/email'
import { timeAgo } from '../lib/format'
import { canManageTeam } from '../lib/permissions'
import type { SeatRole } from '../lib/types'
import { Avatar, Badge, Button, Card, Input, Meter, Modal, Select } from '../components/ui'
import { IconPlus } from '../components/icons'
import './pages.css'

const ROLE_OPTIONS: { value: SeatRole; label: string }[] = [
  { value: 'admin', label: 'Admin — manage team & billing' },
  { value: 'recruiter', label: 'Recruiter — full pipeline access' },
  { value: 'viewer', label: 'Viewer — read-only' },
]

// The compact per-row picker uses short labels; the modal uses the long ones.
const ROLE_SHORT: { value: SeatRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'recruiter', label: 'Recruiter' },
  { value: 'viewer', label: 'Viewer' },
]

export function Team() {
  const { team, session, billing, inviteMember, removeMember, changeMemberRole, resendInvite } = useStore()
  const plan = planById(billing.planId)
  const domain = session.recruiter.domain
  const canManage = canManageTeam(session.recruiter.seatRole)

  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [local, setLocal] = useState('')
  const [role, setRole] = useState<SeatRole>('recruiter')
  const [error, setError] = useState('')
  const [sending, setSending] = useState(false)
  // Per-row busy flag (id being mutated) + a row-level error to surface failures.
  const [busyId, setBusyId] = useState<string | null>(null)
  const [rowError, setRowError] = useState('')
  // Transient confirmation after a resend ("Invite re-sent").
  const [resentId, setResentId] = useState<string | null>(null)
  // Transient success toast after a fresh invite goes out ("Invite sent · <email>").
  const [sentTo, setSentTo] = useState('')

  function flashSent(email: string) {
    setSentTo(email)
    window.setTimeout(() => setSentTo((cur) => (cur === email ? '' : cur)), 4500)
  }

  const seatsUsed = team.length
  const seatCap = plan.seats // null = unlimited
  const atCap = seatCap != null && seatsUsed >= seatCap

  function reset() {
    setName('')
    setLocal('')
    setRole('recruiter')
    setError('')
  }

  async function submit() {
    if (!name.trim()) return setError('Add a name.')
    if (!local.trim()) return setError('Add the part before @.')
    const email = `${local.trim()}@${domain}`
    setSending(true)
    setError('')
    try {
      const { emailDelivered } = await inviteMember(name.trim(), email, role)
      setOpen(false)
      reset()
      if (emailDelivered) {
        flashSent(email)
      } else {
        setRowError('Invite created, but the email could not be sent — use “Resend” once email is configured.')
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send the invite.')
    } finally {
      setSending(false)
    }
  }

  async function onChangeRole(id: string, next: SeatRole) {
    setBusyId(id)
    setRowError('')
    try {
      await changeMemberRole(id, next)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Could not change the role.')
    } finally {
      setBusyId(null)
    }
  }

  async function onResend(id: string) {
    setBusyId(id)
    setRowError('')
    try {
      await resendInvite(id)
      setResentId(id)
      window.setTimeout(() => setResentId((cur) => (cur === id ? null : cur)), 3000)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Could not resend the invite.')
    } finally {
      setBusyId(null)
    }
  }

  async function onRemove(id: string) {
    setBusyId(id)
    setRowError('')
    try {
      await removeMember(id)
    } catch (err) {
      setRowError(err instanceof Error ? err.message : 'Could not remove the teammate.')
    } finally {
      setBusyId(null)
    }
  }

  return (
    <div className="page">
      <div className="page__head">
        <div>
          <h1 className="page__title">Team</h1>
          <p className="page__sub">Everyone at {companyFromDomain(domain)} who shares this verified pipeline.</p>
        </div>
        {canManage && (
          <Button
            variant="primary"
            icon={<IconPlus size={16} />}
            disabled={atCap}
            title={atCap ? 'All seats on your plan are in use' : undefined}
            onClick={() => setOpen(true)}
          >
            Invite teammate
          </Button>
        )}
      </div>

      {!canManage && (
        <p className="page__sub" style={{ marginTop: '-0.4rem', marginBottom: '1rem' }}>
          You have view-only access to the team. Ask an owner or admin to make changes.
        </p>
      )}

      <Card style={{ marginBottom: '1.25rem' }} inset>
        <Meter
          label="Seats"
          used={seatsUsed}
          total={seatCap ?? seatsUsed}
          unit={seatCap == null ? 'unlimited' : 'used'}
          foot={
            seatCap == null
              ? `${plan.name} plan — unlimited seats`
              : atCap
                ? 'All seats in use — upgrade to add more'
                : `${seatCap - seatsUsed} seat${seatCap - seatsUsed === 1 ? '' : 's'} available on the ${plan.name} plan`
          }
        />
      </Card>

      {rowError && (
        <p className="field__error" style={{ marginBottom: '0.75rem' }}>
          <span aria-hidden>⚠</span> {rowError}
        </p>
      )}

      <Card>
        {team.map((m) => {
          const editable = canManage && m.role !== 'owner'
          const busy = busyId === m.id
          return (
            <div key={m.id} className="trow">
              <Avatar hue={m.avatarHue} initials={m.initials} size={40} />
              <div className="trow__id">
                <div className="trow__name">
                  {m.name}
                  {m.status === 'invited' && (
                    <Badge color="var(--c-warn)" bg="var(--c-warn-bg)" dot>
                      {resentId === m.id ? 'Invite re-sent' : 'Invite pending'}
                    </Badge>
                  )}
                </div>
                <div className="trow__email">{m.email}</div>
              </div>
              <span className="trow__email" style={{ minWidth: 90, textAlign: 'right' }}>
                {m.status === 'active' ? `Active ${timeAgo(m.lastActive)}` : 'Not joined yet'}
              </span>

              {editable ? (
                <select
                  className="role-select"
                  value={m.role}
                  disabled={busy}
                  onChange={(e) => onChangeRole(m.id, e.target.value as SeatRole)}
                  aria-label={`Role for ${m.name}`}
                >
                  {ROLE_SHORT.map((o) => (
                    <option key={o.value} value={o.value}>
                      {o.label}
                    </option>
                  ))}
                </select>
              ) : (
                <span className={`role-pill${m.role === 'owner' ? ' role-pill--owner' : ''}`}>{m.role}</span>
              )}

              {editable ? (
                <div className="trow__actions">
                  {m.status === 'invited' && (
                    <Button variant="ghost" size="sm" disabled={busy} onClick={() => onResend(m.id)}>
                      Resend
                    </Button>
                  )}
                  <Button variant="ghost" size="sm" disabled={busy} onClick={() => onRemove(m.id)}>
                    Remove
                  </Button>
                </div>
              ) : (
                <span style={{ width: 70 }} aria-hidden />
              )}
            </div>
          )
        })}
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="Invite a teammate">
        <div className="stack" style={{ gap: '1rem' }}>
          <Input label="Full name" placeholder="Jordan Rivera" value={name} onChange={(e) => setName(e.target.value)} />
          <Input
            label="Work email"
            placeholder="jordan"
            affix={`@${domain}`}
            value={local}
            onChange={(e) => setLocal(e.target.value.replace(/@.*/, ''))}
          />
          <Select label="Role" options={ROLE_OPTIONS} value={role} onChange={(e) => setRole(e.target.value as SeatRole)} />
          {error && <span className="field__error">{error}</span>}
          <div className="row-between" style={{ marginTop: '0.25rem' }}>
            <Button variant="ghost" onClick={() => setOpen(false)} disabled={sending}>
              Cancel
            </Button>
            <Button variant="primary" icon={<IconPlus size={15} />} loading={sending} onClick={submit}>
              Send invite
            </Button>
          </div>
        </div>
      </Modal>

      {sentTo && (
        <div className="toasts" role="status" aria-live="polite">
          <div className="toast toast--ok">
            <span className="toast__check" aria-hidden>
              ✓
            </span>
            <span className="toast__body">
              <b>Invite sent</b>
              <small>{sentTo}</small>
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
